// dependencies
import debounce from "lodash.debounce";
import { GenerateProcess } from "redux-process";
import { push, getMatchedRouteKeyValue } from "yarrr";
import { List, Map } from "immutable";
import { parseISO } from "date-fns";

// ui
import ui from "./ui";

// actions
import { updateUI } from "Actions/ui";

// selectors
import { getPageName, getUI } from "Selectors/ui";
import {
	getSelectedUserID,
	getCurrentQuery,
	getCurrentPage,
	getPageSize,
	getSelectedUser,
	getFiltersMap,
} from "./selectors";

export const changeUI = (keyPath, value) => (dispatch, getState) => {
	const state = getState();
	const pageName = getPageName(state);
	const newKeyPath = [pageName].concat(keyPath);

	dispatch(updateUI(newKeyPath, value));
};

export const searchUsers = () => (dispatch, getState) => {
	const state = getState();
	const query = getCurrentQuery(state);

	dispatch(GenerateProcess("GetUsersSearch", { q: query }));
};

// debounced wrappers for search users
const innerSearchUsers = debounce((dispatch) => setTimeout(() => dispatch(searchUsers()), 0), 0);
const asyncDebouncedSearchUsers = () => innerSearchUsers;

export const updateQuery = (query) => (dispatch) => {
	dispatch(changeUI(["currentQuery"], query));
	dispatch(asyncDebouncedSearchUsers());
};

export const updateFilter = (name, value) => (dispatch) => {
	dispatch(changeUI(["filters", name], value));
};

/**
 * Wrapper for getting breadcrumbs
 */
export const getBreadcrumbs = () => async (dispatch, getState) => {
	const state = getState();
	const pageSize = getPageSize(state);
	const currentPage = getCurrentPage(state);
	const selectedUser = getSelectedUser(state);
	const filters = getFiltersMap(state);

	const firstState = getUI(state).withMutations((mutable) => {
		mutable.set("requests", List());
		mutable.set("requestsCount", 0);
	});

	dispatch(changeUI([], firstState));

	// user id
	const userID = selectedUser.get("id");

	const startTime = filters.get("startTime", null) ? parseISO(filters.get("startTime")).getTime() / 1000 : "";
	const endTime = filters.get("endTime", null) ? parseISO(filters.get("endTime")).getTime() / 1000 : "";
	// get request
	const request = await dispatch(
		GenerateProcess("GetRequests", {
			url: filters.get("url", ""),
			startTime,
			endTime,
			userID,
			limit: pageSize,
			skip: (currentPage - 1) * pageSize,
		})
	);

	// create a next state
	const nextState = getUI(state).withMutations((mutable) => {
		mutable.set("breadcrumbs", request.response.get("requests"));
		mutable.set("breadcrumbsCount", request.response.get("requestsCount"));
	});

	// update ui
	dispatch(changeUI([], nextState));
};

/**
 * Change the breadcrumb page.
 * @param {number} page - new page number
 */
export const changePage = (page) => (dispatch) => {
	dispatch(changeUI(["currentPage"], page));
	dispatch(getBreadcrumbs());
};

export const clearBadLogins = (customerID) => async (dispatch) => {
	try {
		const res = await dispatch(GenerateProcess("ClearCustomerBadLogins", { customerID }));

		const { deleteCount } = res.response;
		dispatch({
			type: "[100] create info toast",
			message: `Cleared ${deleteCount} Bad Logins.`,
			actionMessage: "Dismiss",
		});
	} catch (ermahgerd) {
		//
	}
};

export const fetchUpcomingNotifications = () => (dispatch, getState) => {
	const state = getState();
	const userID = getSelectedUserID(state);
	dispatch(GenerateProcess("fetchUpcomingNotifications", { userID }));
};

/**
 * When a user is selected. Will update the UI and get the user's breadcrumbs.
 */
export const selectUser = (userID) => async (dispatch, getState) => {
	const request = await dispatch(GenerateProcess("GetOneUser", { userID }));
	const userObject = request.response;

	dispatch(changeUI(["selectedUser"], userObject));
	dispatch(getBreadcrumbs());

	const currentUserID = getMatchedRouteKeyValue(getState(), "userID");

	if (currentUserID !== userID) {
		dispatch(push(`/breadcrumbs/${userID}/`));
	}
};

/**
 * Initialize page
 */
export const init = () => async (dispatch, getState) => {
	const state = getState();
	const userID = getSelectedUserID(state);

	const initialUI = ui;
	dispatch(changeUI([], initialUI));

	dispatch(searchUsers());
	if (userID) {
		dispatch(selectUser(userID));
	}
};
